import React, { Dispatch, SetStateAction, useContext, useEffect } from 'react'

import { VoidFunction } from '@js/common/types/FunctionTypes'

import { QuizContext } from '@f/applications/QuizApp/context/QuizContext'
import {
  POST_ANIMATION_TIME_TO_REDIRECT_MS,
  QUIZ_CONFIG_CONVERSION_TYPES,
  TRANSITION_ANIMATION_TIME_MS
} from '@f/applications/QuizApp/partials/quizConstants'
import { QuizStep } from '@f/applications/QuizApp/partials/types'
import { isHouseTypeQuiz } from '@f/applications/QuizApp/partials/utils'
import {
  QuizTransition,
  TransitionState,
  useQuizTransition
} from '@f/applications/QuizApp/questions/checkout/checkoutTransitionOut/useQuizTransition'
import { ValidationLoader } from '@f/applications/QuizApp/questions/checkout/checkoutTransitionOut/ValidationTransitionPresentation'

export interface TransitionProps {
  visible: boolean
  step: TransitionType
}

const setAnimationLoadingTimeout = (
  transition: QuizTransition,
  animationEndCallback: VoidFunction
) =>
  ({
    idle: null,
    animatingValidation: () => {
      window.setTimeout(() => {
        transition.startAccountCreationAnimation()
      }, TRANSITION_ANIMATION_TIME_MS)
    },
    animatingAccountCreation: () =>
      window.setTimeout(() => {
        transition.completeTransition()
      }, TRANSITION_ANIMATION_TIME_MS),
    complete: () =>
      window.setTimeout(() => {
        animationEndCallback()
      }, POST_ANIMATION_TIME_TO_REDIRECT_MS)
  }[transition.state]?.())

type TransitionType = 'success' | 'validation'

interface CheckoutTransitionOutProps {
  setDisplayTransitionOut: Dispatch<SetStateAction<TransitionProps>>
  transitionType: TransitionType
  onTransitionEnd: () => void
}

const getTransitionInitialState = (type: TransitionType): TransitionState =>
  ({
    validation: 'idle' as const,
    success: 'animatingAccountCreation' as const
  }[type])

const financeInterestLoader = {
  label: 'Starte Finanzierungs-Assistenten',
  icon: 'piggybank-64px'
}

const accountCreationLoader = {
  label: 'Account wird erstellt',
  icon: 'user-16px'
}

const valuationInterestLoader = {
  label: 'Starte Bewertungs-Assistent',
  icon: 'house-question-mark-64px'
}

export const CheckoutTransitionOut = ({
  setDisplayTransitionOut,
  transitionType,
  onTransitionEnd
}: CheckoutTransitionOutProps) => {
  const transitionInitialState = getTransitionInitialState(transitionType)
  const transition = useQuizTransition(transitionInitialState)
  const {
    data: { exclusiveVendorNid, steps, conversionType },
    tracking: {
      flow: {
        flow_information: { flow_type }
      }
    }
  } = useContext(QuizContext)

  const isIMCheckout =
    window.__DOMAIN__ === 'immonet' ||
    window.__DOMAIN__ === 'immowelt' ||
    window.__DOMAIN__ === 'bauen'

  const hasFinancialInterest =
    steps.find(
      (step: QuizStep) => step.id === 'financing_consultation_interest'
    )?.values[0].value === 'yes'

  const sellerQuizPostAllowedValues = ['house', 'apartment', 'maybe']

  const valuationInterestValue =
    steps
      ?.find((step: QuizStep) => step.id === 'valuation_interest')
      ?.values[0]?.value?.find(v => v.name === 'valuation-interest')?.value ||
    ''

  const redirectToSellerQuizPost = sellerQuizPostAllowedValues.includes(
    valuationInterestValue
  )

  const getSecondLoaderConfig = () => {
    if (!isIMCheckout && redirectToSellerQuizPost) {
      return valuationInterestLoader
    }

    return hasFinancialInterest ? financeInterestLoader : accountCreationLoader
  }

  const secondLoaderConfig = getSecondLoaderConfig()

  useEffect(() => {
    if (
      flow_type === 'finance-quiz-standalone' ||
      flow_type === 'finance-quiz-standalone-paid'
    ) {
      setDisplayTransitionOut(prev => ({ ...prev, visible: false }))
    } else {
      if (transition.state === 'idle') {
        transition.startValidationAnimation()
      }
    }

    const loadingTimeout = setAnimationLoadingTimeout(transition, () => {
      onTransitionEnd()
    })

    return () => {
      if (loadingTimeout) {
        clearTimeout(loadingTimeout)
      }
    }
  }, [transition.state])

  if (
    exclusiveVendorNid ||
    conversionType === QUIZ_CONFIG_CONVERSION_TYPES.sellerConversion
  ) {
    return (
      <div className='f-pos-rel f-m-t-030'>
        <div className='spinner'></div>
      </div>
    )
  }

  if (transition.state === 'idle') {
    return (
      <div className='[ f-overflow f-pos-ab f-c-ba-bac025 ] QuizApp-transition'></div>
    )
  }

  return transition.state === 'complete' ? (
    <ValidationLoader
      secondLoaderConfig={secondLoaderConfig}
      isDataValidation={false}
      isAccountCreation={true}
    />
  ) : (
    <ValidationLoader
      isDataValidation={transition.state === 'animatingValidation'}
      isAccountCreation={transition.state === 'animatingAccountCreation'}
      secondLoaderConfig={secondLoaderConfig}
    />
  )
}
